import React from 'react';
import styled from 'styled-components';
import DatePicker from 'react-date-picker';
import { Body5, Body7, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { createTimeInstance } from '@utils/time';
import { DATE_RANGE_PRESETS, DATE_RANGE_PRESET_LABELS, toDateValue, toIsoDate } from '@utils/dateRange';
import { useCurrentUser } from '@hooks/user/useCurrentUser';
import { specDisplayName, formatStatus, formatSource, formatDateTime, formatUser } from './format';
import { SpecStatusBadge, LiveBadge } from './SpecStatusBadge';
import { useScoreSpecs } from './useScoreSpecs';
import { useListFilterUrl } from './useListFilterUrl';
import {
  LIVE_ALL,
  LIVE_OFF,
  LIVE_ON,
  authorOptions,
  isFiltering,
  isOnlyMine,
  shouldOfferAuthorFilter,
  updatedAtOf,
} from './listFilters';
import { CONTROL_HEIGHT, FilterGroup, GroupLabel, SegmentedControl, STATUS_SEGMENTS } from './SegmentedControl';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';

// ---- filter bar (Option A: labelled segmented controls) ----
const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px 24px;
  align-items: flex-end;
`;

const SearchInput = styled.input`
  height: ${CONTROL_HEIGHT};
  padding: 0 12px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.text};
  font-size: 13px;
  min-width: 220px;

  &::placeholder {
    color: ${({ theme }) => theme.themeColors.secondaryText};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 1px;
  }
`;

// A bare styled select rather than @ui/inputs' Select: that one is a full-width labelled form field
// (38px, its own <label>, a hint slot), and dropping it into this row would claim a whole line and
// sit 2px shorter than everything beside it. Same reasoning — and very nearly the same rule — as
// feedView/TableControls.js, which defines its own compact select for a filter bar.
const FilterSelect = styled.select`
  height: ${CONTROL_HEIGHT};
  width: 190px;
  padding: 0 8px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.text};
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: 1px;
  }

  option {
    color: ${({ theme }) => theme.themeColors.text};
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }
`;

// The custom window sits on its own row BELOW the filter bar. Revealing two pickers inside a
// wrapping flex row would re-flow the five controls above them every time Custom is picked.
const CustomRangeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const DatePickerWrapper = styled.div`
  .react-date-picker__wrapper {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    border-radius: 4px;
    padding: 8px;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }

  .react-date-picker__inputGroup__input {
    color: ${({ theme }) => theme.themeColors.text};
  }

  .react-calendar {
    background: ${({ theme }) => theme.themeColors.appBackground};
    color: ${({ theme }) => theme.themeColors.text};
  }

  .react-calendar button {
    background: ${({ theme }) => theme.themeColors.appBackground} !important;
    color: ${({ theme }) => theme.themeColors.text} !important;
  }

  .react-calendar button:enabled:hover {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton} !important;
    color: ${({ theme }) => theme.themeColors.buttonText} !important;
  }

  .react-calendar__tile--active {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton} !important;
    color: ${({ theme }) => theme.themeColors.buttonText} !important;
  }
`;

// ---- table ----
// Name | Version | Status | Live | Created by | Updated
const GRID_COLUMNS =
  'minmax(200px, 2.4fr) minmax(70px, 0.6fr) minmax(120px, 1fr) minmax(90px, 0.8fr) minmax(110px, 1fr) minmax(110px, 1fr)';

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.div`
  min-width: 860px;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 16px;
  padding: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.border};
`;

// A real link, not a div with a click handler: the row opens in a new tab, and an anchor gives the
// middle button, cmd-click and "open in new tab" for free, with no keyboard shim to keep in step.
const Row = styled.a`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: 16px;
  align-items: center;
  padding: 12px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  color: inherit;
  text-decoration: none;
  transition: background 0.12s ease;

  &:hover {
    background: ${({ theme }) => theme.themeColors.hoverComponentBackground || theme.themeColors.componentNoOpacity};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: -2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SOURCE_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'api', label: formatSource('api') },
  { key: 'seed', label: formatSource('seed') },
];

const LIVE_SEGMENTS = [
  { key: LIVE_ALL, label: 'All' },
  { key: LIVE_ON, label: 'On' },
  { key: LIVE_OFF, label: 'Off' },
];

// Narrowest-but-one first, then widening. Not every preset the shared module defines — the order is
// per-surface on purpose, which is why the document vault keeps its own list.
const RANGE_PRESET_ORDER = [
  DATE_RANGE_PRESETS.all,
  DATE_RANGE_PRESETS.last1,
  DATE_RANGE_PRESETS.last7,
  DATE_RANGE_PRESETS.last30,
  DATE_RANGE_PRESETS.qtd,
  DATE_RANGE_PRESETS.ytd,
  DATE_RANGE_PRESETS.custom,
];

export const SpecList = () => {
  // Optional-chained: currentUser is null until the session resolves. The hook resolves the Mine
  // default against it at match time, so a null here means "not narrowed yet", never an empty table.
  const currentUserId = useCurrentUser()?.id ?? null;
  // The filters live in the URL, so a reload, a shared link and browser-back from a spec all land
  // on the same view. With no query string the list opens on the reader's own specs (see
  // LIST_DEFAULT_FILTERS): most visits here are to a spec the reader is in the middle of authoring,
  // and the store carries everyone's — so the default answers "where was I" rather than "what
  // exists". All authors is one pick away, and the control stays on screen to say the list is
  // narrowed. Only the list: the fetch screen shares the specs hook and wants every spec.
  const { filters: urlFilters, setFilters: setUrlFilters } = useListFilterUrl();
  const { specs, visible, isLoading, error, filters, setFilters, reload } = useScoreSpecs({
    currentUserId,
    filters: urlFilters,
    setFilters: setUrlFilters,
  });

  if (isLoading) {
    return <Body5 isLowContrast>Loading score specs…</Body5>;
  }

  if (error) {
    return (
      <div>
        <Body5>{error}</Body5>
        <Container top={12}>
          <SkeletonButton onClick={reload}>Retry</SkeletonButton>
        </Container>
      </div>
    );
  }

  // A row opens the record, not the editor. Clicking a name is a question about what the spec IS —
  // the whole row including the operational columns this table only summarises — and the fetch view
  // answers that and carries "Open in the advanced editor" for when the answer is "I need to change
  // it". Landing straight in the editor made the read case pay the editor's cost every time.
  //
  // In a new tab: the list is the view the reader comes back to, filters and scroll and all, and
  // walking a dozen specs through one tab meant re-finding the row after every one.
  const specHref = (spec) => `${ROUTES.getScoreSpec}/${spec.score_spec_id}`;
  // status/source are server-side filters, so an empty result can't be read off the list length —
  // key the message off whether any filter is actually applied. The opening state is itself a
  // filter, and gets its own sentence: the reader has not touched anything, so "these filters"
  // would point at nothing they can see. A store with no specs at all is still an empty store,
  // whoever is asking.
  const hasActiveFilter = isFiltering(filters);
  const storeIsEmpty = (specs || []).length === 0;
  const emptyMessage =
    !hasActiveFilter || (isOnlyMine(filters) && storeIsEmpty)
      ? 'No score specs yet. Create one to get started.'
      : isOnlyMine(filters)
      ? "None of these specs are yours. Pick All authors to see everyone's."
      : 'No score specs match these filters.';

  // Built from `specs`, not `visible`: options taken from the already-filtered rows would collapse
  // to the one author the moment you picked them.
  const authorChoices = authorOptions(specs, currentUserId, filters.author);
  const offerAuthorFilter = shouldOfferAuthorFilter(specs, filters.author);
  const isCustomRange = filters.rangePreset === DATE_RANGE_PRESETS.custom;

  return (
    <div>
      {/* The three entry points that used to sit above this filter row — fetch / paste / create —
          are rendered by the page now, on the back-arrow bar. */}
      <FilterRow>
        <SegmentedControl
          label="Status"
          options={STATUS_SEGMENTS}
          active={filters.status}
          onSelect={(status) => setFilters({ status })}
          withDots
        />
        <SegmentedControl
          label="Source"
          options={SOURCE_CHIPS}
          active={filters.source}
          onSelect={(source) => setFilters({ source })}
        />
        {/* "Live firing", not "Live": the column header already says Live, and two identical strings
            on one screen makes it ambiguous which one a click or a screen reader means — the same
            rule that names the author control. Client-side, like author and the updated window. */}
        <SegmentedControl
          label="Live firing"
          options={LIVE_SEGMENTS}
          active={filters.live}
          onSelect={(live) => setFilters({ live })}
        />
        {/* "Author", not "Created by": the column header already says Created by, and two identical
            strings on one screen makes it ambiguous which one a click or a screen reader means. */}
        {offerAuthorFilter && (
          <FilterGroup>
            <GroupLabel as="label" htmlFor="score-spec-author">
              Author
            </GroupLabel>
            <FilterSelect
              id="score-spec-author"
              aria-label="Filter by author"
              value={filters.author}
              onChange={(e) => setFilters({ author: e.target.value })}
            >
              {authorChoices.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>
        )}
        <FilterGroup>
          <GroupLabel as="label" htmlFor="score-spec-updated">
            Updated in
          </GroupLabel>
          <FilterSelect
            id="score-spec-updated"
            aria-label="Filter by when the spec was updated"
            value={filters.rangePreset}
            onChange={(e) => {
              const rangePreset = e.target.value;
              // Leaving Custom drops the dates with it. Keeping them would mean re-picking Custom
              // silently re-applied a window the operator had already moved on from.
              setFilters(
                rangePreset === DATE_RANGE_PRESETS.custom
                  ? { rangePreset }
                  : { rangePreset, rangeFrom: null, rangeTo: null }
              );
            }}
          >
            {RANGE_PRESET_ORDER.map((preset) => (
              <option key={preset} value={preset}>
                {DATE_RANGE_PRESET_LABELS[preset]}
              </option>
            ))}
          </FilterSelect>
        </FilterGroup>
        <FilterGroup>
          <GroupLabel as="label" htmlFor="score-spec-search">
            Search
          </GroupLabel>
          <SearchInput
            id="score-spec-search"
            aria-label="Search score specs"
            placeholder="Search name…"
            value={filters.q}
            onChange={(e) => setFilters({ q: e.target.value })}
          />
        </FilterGroup>
      </FilterRow>

      {isCustomRange && (
        <Container top={12}>
          <CustomRangeRow role="group" aria-label="Custom updated window">
            <Body7 isLowContrast>From</Body7>
            <DatePickerWrapper>
              <DatePicker
                name="updated-from"
                aria-label="Updated from"
                format="y-MM-dd"
                value={toDateValue(filters.rangeFrom)}
                onChange={(date) => setFilters({ rangeFrom: toIsoDate(date) })}
              />
            </DatePickerWrapper>
            <Body7 isLowContrast>To</Body7>
            <DatePickerWrapper>
              <DatePicker
                name="updated-to"
                aria-label="Updated to"
                format="y-MM-dd"
                value={toDateValue(filters.rangeTo)}
                onChange={(date) => setFilters({ rangeTo: toIsoDate(date) })}
              />
            </DatePickerWrapper>
          </CustomRangeRow>
        </Container>
      )}

      <Container top={16}>
        {visible.length === 0 ? (
          <Body5 isLowContrast>{emptyMessage}</Body5>
        ) : (
          <TableScroll>
            <Table>
              <HeaderRow>
                <Body7 isLowContrast>Name</Body7>
                <Body7 isLowContrast>Version</Body7>
                <Body7 isLowContrast>Status</Body7>
                <Body7 isLowContrast>Live</Body7>
                <Body7 isLowContrast>Created by</Body7>
                <Body7 isLowContrast>Updated</Body7>
              </HeaderRow>
              {visible.map((spec) => (
                <Row
                  key={spec.score_spec_id}
                  href={specHref(spec)}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${specDisplayName(spec)} — ${formatStatus(spec.status)}${
                    spec.live_enabled ? ', live' : ''
                  }`}
                >
                  <Body5 bold>{specDisplayName(spec)}</Body5>
                  <Body7 isLowContrast>v{spec.version}</Body7>
                  <span>
                    <SpecStatusBadge status={spec.status} />
                  </span>
                  <span>
                    <LiveBadge live={spec.live_enabled} />
                  </span>
                  <Body7 isLowContrast>{spec.created_by == null ? 'Seed (system)' : formatUser(spec.created_by)}</Body7>
                  <Body7 isLowContrast title={formatDateTime(updatedAtOf(spec))}>
                    {updatedAtOf(spec) ? createTimeInstance(updatedAtOf(spec)).fromNow() : '—'}
                  </Body7>
                </Row>
              ))}
            </Table>
          </TableScroll>
        )}
      </Container>
    </div>
  );
};
